import React from "react";
import { Sparkles } from "lucide-react";
import { playMagicReveal } from "../../utils/sounds";
import PaymentTrustBadge from "../PaymentTrustBadge";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const MagicPaywall = ({
  showPaywall, setShowPaywall,
  buyingPlan, setBuyingPlan,
  setUsage, setError,
  paywallTeamOpen, setPaywallTeamOpen,
  paywallTeamCode, setPaywallTeamCode,
  setTeamMode, setTeamCode,
}) => {
  if (!showPaywall) return null;

  const plans = [
    { id: "starter", name: "Starter", price: 49, label: "Try the magic", popular: false },
    { id: "popular", name: "Party Planner", price: 99, label: "Most Popular", popular: true },
    { id: "pro", name: "DoozyGo Pro", price: 249, label: "Best Value", popular: false },
  ];

  const handleBuyPlan = async (plan) => {
    setBuyingPlan(plan.id);
    try {
      const res = await fetch(`${API_URL}/api/magic/buy-pass`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan_id: plan.id }),
      });
      const data = await res.json();
      if (res.ok && data.order_id && data.key_id) {
        const options = {
          key: data.key_id,
          amount: data.amount,
          currency: data.currency || "INR",
          name: "DoozyGo Magic",
          description: `${plan.name} Pass`,
          order_id: data.order_id,
          handler: async (paymentResponse) => {
            try {
              const verifyRes = await fetch(`${API_URL}/api/magic/verify-pass-payment`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  razorpay_order_id: paymentResponse.razorpay_order_id,
                  razorpay_payment_id: paymentResponse.razorpay_payment_id,
                  razorpay_signature: paymentResponse.razorpay_signature,
                }),
              });
              if (verifyRes.ok) {
                setUsage(prev => ({ ...prev, has_pass: true, pass_name: plan.name }));
                setShowPaywall(false);
                setError("");
                playMagicReveal();
              } else { setError("Payment verification failed."); }
            } catch (e) { console.debug('[Magic] Payment verify error:', e.message); setError("Payment verification failed."); }
          },
          theme: { color: "#E83E8C" },
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
      } else { setError(data.detail || "Failed to create order"); }
    } catch (e) { console.debug('[Magic] Payment error:', e.message); setError("Payment failed."); }
    setBuyingPlan("");
  };

  const handleTeamActivate = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const code = paywallTeamCode.trim();
    if (code) {
      setTeamMode(true);
      setTeamCode(code);
      sessionStorage.setItem("dg_team", "1");
      sessionStorage.setItem("dg_team_code", code);
      setPaywallTeamOpen(false);
      setShowPaywall(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowPaywall(false)} data-testid="magic-paywall">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
        <div className="text-center mb-5">
          <div className="w-16 h-16 bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] rounded-full flex items-center justify-center mx-auto mb-3">
            <Sparkles size={28} className="text-white" />
          </div>
          <h3 className="text-xl font-bold text-[#1E1B4B]">Get Your DoozyGo Magic Pass</h3>
          <p className="text-sm text-[#475569] mt-1">Visualize your dream party decoration before booking</p>
        </div>

        <div className="space-y-3">
          {plans.map(plan => (
            <button
              key={plan.id}
              disabled={buyingPlan === plan.id}
              onClick={() => handleBuyPlan(plan)}
              className={`w-full p-4 rounded-xl border-2 text-left transition-all relative ${
                plan.popular ? 'border-[#E83E8C] bg-[#E83E8C]/5 hover:bg-[#E83E8C]/10' : 'border-gray-200 hover:border-[#E83E8C]/50'
              }`}
              data-testid={`buy-pass-${plan.id}`}
            >
              {plan.popular && (
                <span className="absolute -top-2.5 left-4 px-2 py-0.5 bg-[#E83E8C] text-white text-[10px] font-bold rounded-full">MOST POPULAR</span>
              )}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold text-[#1E1B4B]">{plan.name}</p>
                  <p className="text-xs text-[#94a3b8]">{plan.label}</p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-[#E83E8C]">Rs {plan.price}</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-4 space-y-2">
          <PaymentTrustBadge variant="compact" />
          <p className="text-[10px] text-[#94a3b8] text-center">Amount adjustable against your party booking</p>
        </div>
        <button onClick={() => setShowPaywall(false)} className="text-xs text-[#94a3b8] hover:text-[#475569] mt-3 block mx-auto">Maybe later</button>

        <div className="mt-4 pt-3 border-t border-gray-100">
          {!paywallTeamOpen ? (
            <button onClick={(e) => { e.stopPropagation(); setPaywallTeamOpen(true); }} className="text-[10px] text-[#94a3b8] hover:text-[#475569] block mx-auto" data-testid="paywall-team-toggle">Have a team code?</button>
          ) : (
            <div className="flex gap-2 mt-1" onClick={e => e.stopPropagation()}>
              <input type="text" value={paywallTeamCode} onChange={(e) => setPaywallTeamCode(e.target.value)} placeholder="Enter team code" className="flex-1 px-3 py-2 text-sm rounded-lg border-2 border-gray-200 focus:border-emerald-500 outline-none" data-testid="paywall-team-input"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); document.querySelector('[data-testid="paywall-team-submit"]')?.click(); } }}
              />
              <button type="button" data-testid="paywall-team-submit" className="px-5 py-2 bg-emerald-500 text-white text-sm rounded-lg font-bold hover:bg-emerald-600 shrink-0"
                onClick={handleTeamActivate}
              >Activate</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
